import { Dialog } from "@headlessui/react"
import { useState } from "react"
import { X } from "lucide-react"
import { supabase } from "../../supabaseClient"

interface Props {
  isOpen: boolean
  onClose: () => void
  onSuccess: (job: any) => void
  onError: (msg: string) => void
}

export default function NewJobModal({ isOpen, onClose, onSuccess, onError }: Props) {
  const [taskName, setTaskName] = useState("")
  const [clientName, setClientName] = useState("")
  const [company, setCompany] = useState("")
  const [hourlyRate, setHourlyRate] = useState("")
  const [loading, setLoading] = useState(false)

  const handleCreate = async () => {
    if (!taskName || !hourlyRate) { onError("Job name and hourly rate are required."); return }
    setLoading(true)
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError || !user) { onError("User not authenticated."); setLoading(false); return }

    const { data, error } = await supabase.from("work_tasks")
      .insert([{ task_name: taskName, client_name: clientName || null, company: company || null, hourly_rate: Number(hourlyRate), user_id: user.id }])
      .select().single()

    if (error) { onError(error.message); setLoading(false); return }
    onSuccess(data)
    setTaskName(""); setClientName(""); setCompany(""); setHourlyRate("")
    setLoading(false); onClose()
  }

  const inputCls = "w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-neutral-800 px-4 py-2.5 text-sm text-black dark:text-white placeholder-gray-400 focus:outline-none focus:border-[#2563EB] transition"

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50">
      <div className="flex items-center justify-center min-h-screen bg-black/50 px-4">
        <Dialog.Panel className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-6 w-full max-w-md shadow-xl">
          <div className="flex items-center justify-between mb-5">
            <Dialog.Title className="text-base font-semibold text-black dark:text-white">Create new job</Dialog.Title>
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition">
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>

          <div className="space-y-3">
            <input placeholder="Job name *" value={taskName} onChange={e => setTaskName(e.target.value)} className={inputCls} />
            <input placeholder="Client name" value={clientName} onChange={e => setClientName(e.target.value)} className={inputCls} />
            <input placeholder="Company" value={company} onChange={e => setCompany(e.target.value)} className={inputCls} />
            <input type="number" placeholder="Hourly rate ($) *" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} className={inputCls} />
          </div>

          <button
            onClick={handleCreate}
            disabled={loading}
            className="mt-5 w-full bg-[#2563EB] text-white py-2.5 rounded-xl text-sm font-medium hover:bg-[#1d4ed8] transition disabled:opacity-60"
          >
            {loading ? "Creating…" : "Create job"}
          </button>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}
